import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import AdminAttachmentRepairButton from '../components/AdminAttachmentRepairButton';
import type {
  TicketsWithoutS3AttachmentsResponse,
  TicketWithoutS3AttachmentsItem,
  TicketAttachmentWithoutS3Occurrence,
  KnowledgeArticlesWithMissingImagesResponse,
  KnowledgeArticleWithMissingImagesItem,
} from '../types';
import type { RepairKind } from '../utils/attachmentRepair';
import { getAuthToken } from '../utils/authToken';

const TICKETS_PAGE_SIZE = 50;
const KB_PAGE_SIZE = 25;

const kindLabel = (kind: string): string => {
  switch (kind) {
    case 'ticket_attachment':
      return 'Anexo do ticket';
    case 'ticket_cfv_file':
      return 'Campo customizado (ficheiro único)';
    case 'ticket_cfv_files_item':
      return 'Campo customizado (lista de ficheiros)';
    default:
      return kind;
  }
};

const getRepairParams = (
  occurrence: TicketAttachmentWithoutS3Occurrence
): { kind: RepairKind; targetId: number; fileIndex?: number | null } | null => {
  if (occurrence.kind === 'ticket_attachment' && occurrence.attachment_id != null) {
    return { kind: 'ticket_attachment', targetId: occurrence.attachment_id };
  }
  if (
    (occurrence.kind === 'ticket_cfv_file' || occurrence.kind === 'ticket_cfv_files_item') &&
    occurrence.custom_field_value_id != null
  ) {
    return {
      kind: 'ticket_cfv',
      targetId: occurrence.custom_field_value_id,
      fileIndex: occurrence.file_index ?? null,
    };
  }
  return null;
};

const kbSourceLabel = (source: string): string => {
  if (source === 'article_body_img') return 'Imagem no corpo do artigo';
  return source;
};

const TicketAttachmentsWithoutS3: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showAlert } = useModal();
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsData, setTicketsData] = useState<TicketsWithoutS3AttachmentsResponse | null>(null);
  const [skip, setSkip] = useState(0);
  const [ticketIdInput, setTicketIdInput] = useState('');
  const [appliedTicketId, setAppliedTicketId] = useState('');
  const [creatorInput, setCreatorInput] = useState('');
  const [appliedCreator, setAppliedCreator] = useState('');
  const [takerInput, setTakerInput] = useState('');
  const [appliedTaker, setAppliedTaker] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loadingKb, setLoadingKb] = useState(false);
  const [kbData, setKbData] = useState<KnowledgeArticlesWithMissingImagesResponse | null>(null);
  const [kbSkip, setKbSkip] = useState(0);
  const [kbArticleIdInput, setKbArticleIdInput] = useState('');
  const [appliedKbArticleId, setAppliedKbArticleId] = useState('');
  const [kbExpanded, setKbExpanded] = useState<Record<number, boolean>>({});
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      setLoadingTickets(true);
      try {
        const params: Record<string, string | number> = { skip, limit: TICKETS_PAGE_SIZE };
        const tid = appliedTicketId.trim();
        if (tid && /^\d+$/.test(tid)) {
          params.ticket_id = parseInt(tid, 10);
        }
        const creator = appliedCreator.trim();
        if (creator) {
          params.created_by_name = creator;
        }
        const taker = appliedTaker.trim();
        if (taker) {
          params.taken_by_name = taker;
        }
        const res = await axios.get<TicketsWithoutS3AttachmentsResponse>(
          `${API_URL}/api/attachments/admin/tickets-without-s3`,
          {
            params,
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        );
        if (!cancelled) setTicketsData(res.data);
      } catch (e: unknown) {
        const ax = e as { response?: { status?: number; data?: { detail?: string } } };
        if (ax.response?.status === 403) {
          showAlert({ title: 'Acesso negado', message: 'Apenas administradores.', variant: 'danger' });
        } else {
          showAlert({
            title: 'Erro',
            message: ax.response?.data?.detail || 'Não foi possível carregar a lista.',
            variant: 'danger',
          });
        }
        if (!cancelled) setTicketsData(null);
      } finally {
        if (!cancelled) setLoadingTickets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.role, skip, appliedTicketId, appliedCreator, appliedTaker, reloadTick, showAlert]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      setLoadingKb(true);
      try {
        const params: Record<string, string | number> = { skip: kbSkip, limit: KB_PAGE_SIZE };
        const aid = appliedKbArticleId.trim();
        if (aid && /^\d+$/.test(aid)) {
          params.article_id = parseInt(aid, 10);
        }
        const res = await axios.get<KnowledgeArticlesWithMissingImagesResponse>(
          `${API_URL}/api/knowledge-base/admin/articles-with-missing-images`,
          {
            params,
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        );
        if (!cancelled) setKbData(res.data);
      } catch (e: unknown) {
        const ax = e as { response?: { status?: number; data?: { detail?: string } } };
        if (ax.response?.status === 403) {
          showAlert({ title: 'Acesso negado', message: 'Apenas administradores.', variant: 'danger' });
        } else {
          showAlert({
            title: 'Erro',
            message: ax.response?.data?.detail || 'Não foi possível carregar os artigos da base.',
            variant: 'danger',
          });
        }
        if (!cancelled) setKbData(null);
      } finally {
        if (!cancelled) setLoadingKb(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.role, kbSkip, appliedKbArticleId, reloadTick, showAlert]);

  const toggleExpand = (ticketId: number) => {
    setExpanded((prev) => ({ ...prev, [ticketId]: !prev[ticketId] }));
  };

  const toggleKbExpand = (articleId: number) => {
    setKbExpanded((prev) => ({ ...prev, [articleId]: !prev[articleId] }));
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Card className="m-3">
        <Card.Body>Esta página é exclusiva para administradores.</Card.Body>
      </Card>
    );
  }

  const items: TicketWithoutS3AttachmentsItem[] = ticketsData?.items ?? [];
  const total = ticketsData?.total_tickets ?? 0;
  const canPrev = skip > 0;
  const canNext = skip + TICKETS_PAGE_SIZE < total;
  const kbItems: KnowledgeArticleWithMissingImagesItem[] = kbData?.items ?? [];
  const kbTotal = kbData?.total_articles ?? 0;
  const kbCanPrev = kbSkip > 0;
  const kbCanNext = kbSkip + KB_PAGE_SIZE < kbTotal;

  return (
    <div className="p-3">
      <Card className="mb-3">
        <Card.Header>
          <strong>Tickets com anexos sem vínculo S3</strong>
          <div className="small text-muted mt-1">
            Lista anexos diretos e ficheiros em campos customizados cujo <code>storage_provider</code> não é{' '}
            <code>s3</code> ou sem <code>s3_object_key</code>.
          </div>
        </Card.Header>
        <Card.Body>
          <Form className="row g-2 align-items-end mb-3">
            <div className="col-auto">
              <Form.Label className="small mb-0">Ticket ID (opcional)</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={ticketIdInput}
                onChange={(e) => setTicketIdInput(e.target.value)}
                placeholder="ex: 32"
                style={{ width: 120 }}
              />
            </div>
            <div className="col-auto">
              <Form.Label className="small mb-0">Criado por (opcional)</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={creatorInput}
                onChange={(e) => setCreatorInput(e.target.value)}
                placeholder="nome do criador"
                style={{ width: 220 }}
              />
            </div>
            <div className="col-auto">
              <Form.Label className="small mb-0">Assumido por (opcional)</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={takerInput}
                onChange={(e) => setTakerInput(e.target.value)}
                placeholder="nome de quem assumiu"
                style={{ width: 220 }}
              />
            </div>
            <div className="col-auto">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setSkip(0);
                  setAppliedTicketId(ticketIdInput.trim());
                  setAppliedCreator(creatorInput.trim());
                  setAppliedTaker(takerInput.trim());
                }}
              >
                Aplicar
              </Button>
            </div>
            <div className="col-auto ms-auto small text-muted">
              Total: <strong>{total}</strong> ticket(s) — página {Math.floor(skip / TICKETS_PAGE_SIZE) + 1}
            </div>
          </Form>

          {loadingTickets ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-muted mb-0">Nenhum ticket encontrado com os critérios atuais.</p>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Estado</th>
                  <th>Criado por</th>
                  <th>Assumido por</th>
                  <th>Ocorrências</th>
                  <th>Atualizado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <React.Fragment key={row.ticket_id}>
                    <tr>
                      <td>{row.ticket_id}</td>
                      <td className="text-break">{row.title}</td>
                      <td>
                        <Badge bg="secondary">{row.status}</Badge>
                      </td>
                      <td className="text-break">{row.created_by_name || '-'}</td>
                      <td className="text-break">{row.taken_by_name || '-'}</td>
                      <td>{row.occurrence_count}</td>
                      <td>{new Date(row.updated_at).toLocaleString('pt-BR')}</td>
                      <td className="text-nowrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1"
                          onClick={() => toggleExpand(row.ticket_id)}
                        >
                          {expanded[row.ticket_id] ? 'Ocultar' : 'Detalhes'}
                        </Button>
                        <Link to={`/tickets/${row.ticket_id}`} className="btn btn-sm btn-outline-primary">
                          Abrir ticket
                        </Link>
                      </td>
                    </tr>
                    {expanded[row.ticket_id] && (
                      <tr>
                        <td colSpan={8}>
                          <Table size="sm" bordered className="mb-0 bg-light">
                            <thead>
                              <tr>
                                <th>Tipo</th>
                                <th>Ficheiro</th>
                                <th>Caminho</th>
                                <th>Motivo</th>
                                <th>IDs</th>
                                <th>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.occurrences.map((o, idx) => {
                                const repair = getRepairParams(o);
                                return (
                                <tr key={`${row.ticket_id}-${idx}-${o.path}`}>
                                  <td>{kindLabel(o.kind)}</td>
                                  <td className="text-break">{o.filename}</td>
                                  <td className="text-break small">{o.path}</td>
                                  <td className="small">{o.reason}</td>
                                  <td className="small text-muted">
                                    {o.attachment_id != null && <>att #{o.attachment_id}<br /></>}
                                    {o.custom_field_value_id != null && (
                                      <>cfv #{o.custom_field_value_id} (campo {o.custom_field_id})</>
                                    )}
                                    {o.file_index != null && (
                                      <>
                                        <br />
                                        índice {o.file_index}
                                      </>
                                    )}
                                  </td>
                                  <td className="text-nowrap">
                                    {repair ? (
                                      <AdminAttachmentRepairButton
                                        kind={repair.kind}
                                        targetId={repair.targetId}
                                        fileIndex={repair.fileIndex}
                                        onRepaired={() => setReloadTick((v) => v + 1)}
                                      />
                                    ) : (
                                      <span className="small text-muted">N/A</span>
                                    )}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!canPrev || loadingTickets}
              onClick={() => setSkip((s) => Math.max(0, s - TICKETS_PAGE_SIZE))}
            >
              Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!canNext || loadingTickets}
              onClick={() => setSkip((s) => s + TICKETS_PAGE_SIZE)}
            >
              Seguinte
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <strong>Base de conhecimento com imagens indisponíveis</strong>
          <div className="small text-muted mt-1">
            Lista artigos que ainda referenciam imagens internas da base, mas sem ficheiro acessível no storage.
          </div>
        </Card.Header>
        <Card.Body>
          <Form className="row g-2 align-items-end mb-3">
            <div className="col-auto">
              <Form.Label className="small mb-0">Artigo ID (opcional)</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={kbArticleIdInput}
                onChange={(e) => setKbArticleIdInput(e.target.value)}
                placeholder="ex: 15"
                style={{ width: 120 }}
              />
            </div>
            <div className="col-auto">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setKbSkip(0);
                  setAppliedKbArticleId(kbArticleIdInput.trim());
                }}
              >
                Aplicar
              </Button>
            </div>
            <div className="col-auto ms-auto small text-muted">
              Total: <strong>{kbTotal}</strong> artigo(s) — página {Math.floor(kbSkip / KB_PAGE_SIZE) + 1}
            </div>
          </Form>

          {loadingKb ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : kbItems.length === 0 ? (
            <p className="text-muted mb-0">Nenhum artigo encontrado com imagens indisponíveis.</p>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Categoria</th>
                  <th>Ocorrências</th>
                  <th>Atualizado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {kbItems.map((row) => (
                  <React.Fragment key={row.article_id}>
                    <tr>
                      <td>{row.article_id}</td>
                      <td className="text-break">{row.title}</td>
                      <td>{row.category_name || '-'}</td>
                      <td>{row.occurrence_count}</td>
                      <td>{new Date(row.updated_at).toLocaleString('pt-BR')}</td>
                      <td className="text-nowrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1"
                          onClick={() => toggleKbExpand(row.article_id)}
                        >
                          {kbExpanded[row.article_id] ? 'Ocultar' : 'Detalhes'}
                        </Button>
                        <Link to={`/knowledge-base/${row.article_id}`} className="btn btn-sm btn-outline-primary">
                          Abrir artigo
                        </Link>
                      </td>
                    </tr>
                    {kbExpanded[row.article_id] && (
                      <tr>
                        <td colSpan={6}>
                          <Table size="sm" bordered className="mb-0 bg-light">
                            <thead>
                              <tr>
                                <th>Fonte</th>
                                <th>URL</th>
                                <th>Imagem</th>
                                <th>Motivo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.occurrences.map((o, idx) => (
                                <tr key={`${row.article_id}-${idx}-${o.image_url}`}>
                                  <td>{kbSourceLabel(o.source)}</td>
                                  <td className="text-break small">{o.image_url}</td>
                                  <td className="small">{o.image_name || '-'}</td>
                                  <td className="small">{o.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!kbCanPrev || loadingKb}
              onClick={() => setKbSkip((s) => Math.max(0, s - KB_PAGE_SIZE))}
            >
              Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!kbCanNext || loadingKb}
              onClick={() => setKbSkip((s) => s + KB_PAGE_SIZE)}
            >
              Seguinte
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TicketAttachmentsWithoutS3;
